/**
 * 读取 AIAssistant.chat({ stream: true }) 返回的 reader，增量回调正文与思考过程。
 * reader.read() 返回 { done, value, fullContent, reasoningChunk, fullReasoningContent }
 *
 * onUpdate({ content, reasoning }) — reasoning 仅用于 UI 展示，返回值仅为正文。
 */
import { isZzlStreamDebug, zzlStreamLog } from '@/components/ai/zzl-stream-debug';

function preview(text, max = 60) {
  if (!text) return '';
  const s = String(text).replace(/\s+/g, ' ').trim();
  return s.length <= max ? s : `${s.slice(0, max)}…`;
}

function pickFullContent(reader, chunk, prevFull) {
  if (typeof reader.getFullContent === 'function') {
    const fromReader = reader.getFullContent();
    if (fromReader != null && fromReader !== '') {
      return String(fromReader);
    }
  }
  if (chunk.fullContent != null && chunk.fullContent !== '') {
    return String(chunk.fullContent);
  }
  if (chunk.value) {
    return prevFull + String(chunk.value);
  }
  return prevFull;
}

function pickReasoningContent(reader, chunk) {
  if (typeof reader.getFullReasoningContent === 'function') {
    const fromReader = reader.getFullReasoningContent();
    if (fromReader != null && fromReader !== '') {
      return String(fromReader);
    }
  }
  if (chunk.fullReasoningContent != null && chunk.fullReasoningContent !== '') {
    return String(chunk.fullReasoningContent);
  }
  if (chunk.reasoningChunk) {
    return String(chunk.reasoningChunk);
  }
  return '';
}

/**
 * @param {() => Promise<{ read: Function, cancel?: Function, getFullContent?: Function }>} chatCall
 * @param {(state: { content: string, reasoning: string }) => void} onUpdate
 * @returns {Promise<string>} 仅正文，不含思考过程
 */
export async function readAssistantChatStream(chatCall, onUpdate) {
  const t0 = Date.now();
  let readCount = 0;
  let updateCount = 0;
  let skipCount = 0;
  let lastContent = '';
  let lastReasoning = '';

  zzlStreamLog('readAssistantChatStream start', { debug: isZzlStreamDebug(), t0 });

  const reader = await chatCall();
  zzlStreamLog('reader ready', {
    ms: Date.now() - t0,
    hasRead: !!(reader && reader.read),
    hasCancel: !!(reader && reader.cancel),
    hasGetFullContent: !!(reader && reader.getFullContent),
    hasGetFullReasoningContent: !!(reader && reader.getFullReasoningContent),
    keys: reader ? Object.keys(reader) : [],
  });

  if (!reader || typeof reader.read !== 'function') {
    throw new Error('未能建立流式连接');
  }

  let contentFull = '';
  while (true) {
    const tRead = Date.now();
    const chunk = await reader.read();
    readCount += 1;

    const reasoning = pickReasoningContent(reader, chunk);

    zzlStreamLog(`read #${readCount}`, {
      msSinceStart: Date.now() - t0,
      readMs: Date.now() - tRead,
      done: chunk.done,
      valueLen: chunk.value ? String(chunk.value).length : 0,
      fullContentLen: chunk.fullContent != null ? String(chunk.fullContent).length : null,
      reasoningLen: reasoning.length,
      contentFullLen: contentFull.length,
    });

    if (chunk.done) {
      contentFull = pickFullContent(reader, chunk, contentFull);
      if (!contentFull && typeof reader.getFullContent === 'function') {
        contentFull = reader.getFullContent() || contentFull;
      }
      const finalReasoning = pickReasoningContent(reader, chunk);
      zzlStreamLog('done', {
        msSinceStart: Date.now() - t0,
        readCount,
        updateCount,
        skipCount,
        finalLen: contentFull.length,
        reasoningLen: finalReasoning.length,
      });
      onUpdate({ content: contentFull, reasoning: finalReasoning });
      break;
    }

    const prevContent = contentFull;
    contentFull = pickFullContent(reader, chunk, contentFull);
    const reasoningNow = pickReasoningContent(reader, chunk);

    if (contentFull !== lastContent || reasoningNow !== lastReasoning) {
      updateCount += 1;
      lastContent = contentFull;
      lastReasoning = reasoningNow;
      if (updateCount <= 5 || updateCount % 20 === 0) {
        zzlStreamLog(`onUpdate #${updateCount}`, {
          msSinceStart: Date.now() - t0,
          contentLen: contentFull.length,
          reasoningLen: reasoningNow.length,
          preview: preview((contentFull || reasoningNow).slice(-80)),
        });
      }
      onUpdate({ content: contentFull, reasoning: reasoningNow });
    } else {
      skipCount += 1;
      if (skipCount <= 3 || skipCount % 100 === 0) {
        zzlStreamLog('skip update (unchanged)', {
          skipCount,
          contentLen: contentFull.length,
          reasoningLen: reasoningNow.length,
          contentDelta: contentFull.length - prevContent.length,
        });
      }
    }
  }

  return contentFull;
}
